/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Marcus Biesel <m.biesel@techdivision.com>
 * @author Dominik Boeck <d.boeck@techdivision.com>
 */

import axios from 'axios';
import { useMutation as useTanMutation } from '@tanstack/react-query';
import { getMutationApiEndpoint } from '../../Services/API/EndpointService';
import { errorSignal } from '../../Signals/errorSignal';
import useErrorCodeMapping from '../../Errors/useErrorCodeMapping';

/**
 * @param apiEndpointKey
 * @param signal
 * @param reactQueryOptions
 * @param axiosOptions
 * @returns {UseMutationResult<axios.AxiosResponse<any> | TData, DefaultError, void, unknown>}
 */
export const useMutation = (
  apiEndpointKey,
  signal = null,
  reactQueryOptions = {},
  axiosOptions = {},
) => {
  const { getErrorMessage } = useErrorCodeMapping();
  return useTanMutation({
    mutationKey: [apiEndpointKey],
    mutationFn: (lazyMutationVariables) => axios.post(
      getMutationApiEndpoint(apiEndpointKey),
      lazyMutationVariables,
      { ...axiosOptions },
    )
      .then(({ data }) => {
        if (signal) {
          // eslint-disable-next-line no-param-reassign
          signal.value = data;
        }
        return data;
      })
      .catch((error) => {
        const {
          message,
          errorCodes,
        } = error.response.data;
        errorSignal.signal.value = getErrorMessage(message, errorCodes);
        throw error;
      }),
    ...reactQueryOptions,
  });
};
